import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';

import { V1A_NAMESPACE } from '../constants';
import { useAppVitals, VitalsSvg } from './app-vitals';
import { imageSrc, imageSrcSet } from './media';

const APP_WIDTHS = [640, 1024, 1280, 1600, 2034] as const;
const APP_SIZES = '(min-width: 43rem) 625px, calc(100vw - 2.5rem)';
const STEPS = ['download', 'game', 'play'] as const;
// feature names stay canonical in every locale
const CHIPS = ['FPS Boost', 'Multi Internet', 'Aim Trainer', 'AudioPad', 'Replay X', 'Pro Settings'] as const;

export function AppShowcase() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const imgRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pingRef = useRef<HTMLSpanElement>(null);
  const [built, setBuilt] = useState(false);
  const handleBuild = useCallback(() => setBuilt(true), []);
  useAppVitals({ imgRef, svgRef, pingRef, built, onBuild: handleBuild });

  return (
    <section className='v1-app relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0A0C10,#0E1116)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-app-title'>
      <div className='v1-app__bg' aria-hidden='true' />
      <div className='v1-app__inner mx-auto grid w-full max-w-[90rem] items-center gap-[clamp(2rem,1rem_+_3vw,3.5rem)] px-[var(--gutter)] *:min-w-0 lg:grid-cols-[minmax(0,1fr)_39.0625rem] lg:gap-[clamp(2.5rem,1rem_+_3.5vw,4.5rem)]'>
        <div className='v1-app__copy'>
          <p className='v1-eyebrow m-0 inline-flex items-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal=''>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('app.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-app-title' data-reveal=''>
            <Trans t={t} i18nKey='app.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal=''>
            {t('app.lead')}
          </p>

          <ol className='v1-steps mt-[clamp(1.75rem,1rem_+_2vw,2.5rem)] grid list-none gap-0 p-0 *:min-w-0' data-reveal-group=''>
            {STEPS.map((step, index) => (
              <li key={step} className='v1-step relative grid grid-cols-[3rem_minmax(0,1fr)] gap-4 pb-[1.375rem] last:pb-0'>
                <span className='v1-step__n relative z-[1] grid size-12 place-items-center text-[length:var(--t-micro)] font-bold tracking-[.06em] !text-[#93FF18]'>{`0${index + 1}`}</span>
                <div>
                  <h3 className='v1-step__t mt-1.5 text-[length:1rem] font-semibold tracking-[-.01em] text-[#FFFFFF]'>{t(`app.steps.${step}.title`)}</h3>
                  <p className='v1-step__d mt-[.3125rem] text-[length:var(--t-body)] leading-normal text-[#9AA3B5]'>{t(`app.steps.${step}.text`)}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* role=list: list-style none drops the list semantics in Safari/VoiceOver, as the design notes */}

          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className='v1-app__chips mt-6 flex max-w-[27rem] list-none flex-wrap gap-2 !p-0 [&_li]:px-2.5 [&_li]:py-[.3125rem] [&_li]:text-[length:var(--t-micro)] [&_li]:font-semibold [&_li]:tracking-[.06em] [&_li]:text-[#CBD5E1]' role='list' data-reveal=''>
            {CHIPS.map((chip) => (
              <li key={chip}>{chip}</li>
            ))}
          </ul>
        </div>

        <figure className='v1-app__shot m-0' data-reveal=''>
          <div className='v1-app__frame'>
            <div className='v1-app__chrome' aria-hidden='true'>
              <i />
              <i />
              <i />
              <span>NoPing</span>
              <span className='v1-app__hud'>
                <span className='v1-app__hud-dot' />
                <span className='v1-app__hud-label'>{t('app.connected')}</span>
                <span ref={pingRef} className='v1-app__hud-num tabular' data-app-ping=''>
                  3 ms
                </span>
              </span>
            </div>
            <picture>
              <source type='image/avif' srcSet={imageSrcSet('app-client', APP_WIDTHS, 'avif')} sizes={APP_SIZES} />
              <img ref={imgRef} src={imageSrc('app-client-1024.webp')} srcSet={imageSrcSet('app-client', APP_WIDTHS, 'webp')} sizes={APP_SIZES} width='2034' height='1163' alt={t('app.image_alt')} loading='lazy' decoding='async' />
            </picture>
            {built && <VitalsSvg ref={svgRef} />}
          </div>
          <figcaption className='v1-app__cap mt-3 text-center text-[length:max(.75rem,var(--t-micro))] text-[#8C95A8]'>{t('app.caption')}</figcaption>
        </figure>
      </div>
    </section>
  );
}
